/*
 * One game per day, the same for everybody.
 *
 * The historical last round was already fixed by the calendar — everyone playing
 * on 2 September gets the Great Fire. This does the same for the rounds before
 * it: they are drawn from a generator seeded off the date, so two people playing
 * today are handed the same three places at the same three hours, and their
 * scores are finally comparable. Before this the leaderboard ranked people who
 * had played different games.
 *
 * The day is the player's *local* calendar date, matching `eventForDate`. That
 * also settles an inconsistency: round instants used to be drawn on the UTC
 * date, so anyone playing late in the Americas got a historical round from their
 * own date and ordinary rounds from the next one.
 */

// The calendar day a game belongs to, as YYYYMMDD.
export function dayKey(date = new Date()) {
  return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
}

// Midnight UTC on that same calendar day, which is when a round's hours are
// measured from. The date the player is shown is this one.
export function dayStart(date = new Date()) {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

/*
 * The same day as YYYY-MM-DD, which is how the leaderboard names it.
 *
 * Read off `dayStart` rather than assembled from the date parts a second time,
 * so it cannot drift from the day the rounds are actually generated for — the
 * server derives its own copy from those rounds, and the two have to agree or a
 * score lands on a board nobody is looking at.
 */
export function dayIso(date = new Date()) {
  return new Date(dayStart(date)).toISOString().slice(0, 10);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/*
 * A YYYY-MM-DD written the way a person reads it.
 *
 * Split rather than parsed: `new Date('2026-09-02')` is midnight *UTC*, so
 * formatting it anywhere west of Greenwich prints the 1st. The string already
 * holds the three numbers wanted, and taking them straight out cannot be off by
 * a day anywhere on Earth.
 *
 * The year is dropped when it is the current one, which for a board that is
 * almost always showing the last few days is nearly always.
 */
export function formatDay(iso, currentYear = new Date().getFullYear()) {
  const [year, month, date] = String(iso).split('-').map(Number);
  if (!year || !month || !date) return String(iso);
  return `${date} ${MONTHS[month - 1]}${year === currentYear ? '' : ` ${year}`}`;
}

/*
 * Mulberry32: small, fast, and good enough for placing three points on a globe.
 * What matters here is only that it is deterministic and that nearby seeds do
 * not produce visibly related output — consecutive round indices must not give
 * three neighbouring places.
 */
export function makeRng(seed) {
  let state = seed >>> 0;
  return function next() {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// A stable small number per tier, folded into the seed below.
function tierSalt(tier) {
  let hash = 0;
  for (const character of String(tier)) hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  return hash;
}

/*
 * A round's own generator. The index and the tier are mixed in with large odd
 * multipliers rather than added, so round 2 is not a near neighbour of round 1
 * in seed space.
 *
 * The tier is in there because the boards are separate and the games should be
 * too: with one seed for both, playing Easy — where the constellations are named
 * and the compass is up — would hand you the answers to that day's Hard game.
 */
export function rngForRound(date, roundIndex, tier = '') {
  return makeRng((dayKey(date) + roundIndex * 0x9e3779b1 + tierSalt(tier) * 0x85ebca6b) >>> 0);
}
